import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { get_api_url } from '../../../utils/urls';
import { get_api } from '../../../redux/api_funcs';
import CustomDatePicker from '../../util_components/custom_date_picker';
import * as actions from '../../../redux/action_creators';
import CustomSingleSelect from '../../util_components/custom_single_select';
import { teacherFullNameOf } from '../../../utils/teacher_name';

const categories = [
  { t: 'energy', p: ['energy level'] },
  {
    t: 'welcome',
    p: [
      'warm greeting',
      'friendly smile',
      'welcoming',
      'session breakdown',
      'effective introduction'
    ]
  },
  {
    t: 'interview',
    p: [
      'open ended question',
      'reflections / affirmations',
      'summarizing',
      'non verbal cues',
      'no interruption'
    ]
  },
  { t: 'practice plan', p: ['session plan', 'intensity of practice'] },
  { t: 'practice', p: ['demo and explanation', 'affirmations'] },
  {
    t: 'feedback & de-brief',
    p: ['personalized feedback', 'summarizing the session', 'inviting questions & doubt clearing']
  },
  { t: 'future plan', p: ['future plan'] },
  { t: 'concierge handoff', p: ['handoff'] }
];

const teacherLimit = 10;

function GptSessionQaScores() {
  const [scores, setQaScores] = useState([]);

  const [startDate, setStartDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const psUUID = queryParams.get('student');
  const ptUUID = queryParams.get('teacher');

  const dispatch = useDispatch();
  const teachersList = useSelector((state) => state.teachers.teachers_list);
  const [teacherSelectionList, setTeacherSelectionList] = useState([]);
  const [filterTeacher, setFilterTeacher] = useState('all');

  const handleDateChange = (type, value) => {
    if (type === 'start_date') {
      setStartDate(value);
    } else if (type === 'end_date') {
      setEndDate(value);
    }
  };

  const loadScores = async (teacherVal) => {
    let query = `/v2/crons-tasks/poc/qascores`;
    if (teacherVal && teacherVal !== 'all') {
      query += `?teacher_uuid=${teacherVal}&limit=${teacherLimit}`;
    } else if (psUUID !== null && psUUID !== undefined) {
      query += `?student_uuid=${psUUID}`;
    } else if (ptUUID !== null && ptUUID !== undefined) {
      query += `?teacher_uuid=${ptUUID}&limit=${teacherLimit}`;
    } else {
      query += `?since=${startDate} 00:00:00&till=${endDate} 23:59:59`;
    }

    const url = get_api_url(query, null, true);
    const result = await get_api(url, true);
    const ps = JSON.parse(JSON.stringify(result.data.data).toLowerCase());
    setQaScores(ps);
  };

  useEffect(() => {
    dispatch(actions.get_teachers_list());
    loadScores();
  }, []);

  useEffect(() => {
    const allTeachers = teachersList.filter((t) => t.onboarding_status === 'APPROVED');
    const lteachers = allTeachers.map((teacher) => ({
      value: teacher.uuid,
      label: teacherFullNameOf(teacher)
    }));
    lteachers.sort((a, b) => a.label.localeCompare(b.label));
    lteachers.unshift({ value: 'all', label: 'All' });
    setTeacherSelectionList(lteachers);
  }, [teachersList]);

  const handleTeacherChange = (e) => {
    const { value } = e.target;
    setFilterTeacher(value);
    loadScores(value);
  };

  const handleSubmit = () => {
    loadScores();
  };

  const lookupScore = (rscores, category, point) => {
    const found = rscores.find((row) => row.parameter === point && row.category === category);

    if (found === undefined) {
      // console.log('lookupScore', scores, category, point);

      if (category === 'welcome' && point === 'effective introduction') {
        const c1 = lookupScore(rscores, category, 'coach life story');
        const c2 = lookupScore(rscores, category, 'relavant expertise');
        const c3 = lookupScore(rscores, category, 'students helped');

        return {
          score: c1.score + c2.score + c3.score,
          reason: [c1.reason, c2.reason, c3.reason].join(',')
        };
      }

      return { score: 0, reason: 'No score found' };
    }

    return found;
  };

  const calculateSum = (score) => {
    // old data set, calculate and send
    let sum = 0;
    score.qa_score.forEach((cps) => {
      sum += cps.score;
    });

    return sum;
  };

  return (
    <div className="common-page-container h-full">
      <div className="font-bold text-black text-[30px] mb-5">QA Scores</div>

      {psUUID == null && ptUUID == null && (
        <div className="flex flex-col gap-5">
          <p className="">Select a Teacher or Date</p>

          <div className="flex items-center gap-5">
            <div className="daily-report-item">
              <CustomSingleSelect
                labelClass="hidden"
                label=""
                options={teacherSelectionList}
                name="filter_teacher"
                onChange={handleTeacherChange}
                value={filterTeacher}
                style={{ width: '250px' }}
              />
            </div>

            <div className="daily-report-item">
              <CustomDatePicker
                label="Start Date"
                value={startDate}
                handleChange={(val) => handleDateChange('start_date', val)}
                maxDate={endDate}
              />
            </div>
            <div className="daily-report-item">
              <CustomDatePicker
                label="End Date"
                value={endDate}
                handleChange={(val) => handleDateChange('end_date', val)}
                minDate={startDate}
                maxDate={moment().format('YYYY-MM-DD')}
              />
            </div>
            <button className="mt-[25px]" onClick={handleSubmit} type="button">
              Submit
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 text-sm">
        {(ptUUID != null || filterTeacher !== 'all') &&
          `* Recent ${teacherLimit} sessions are shown for selected teacher`}
      </div>
      <div className="mt-2 text-sm">
        Average Score:{' '}
        {scores.length > 0
          ? (scores.reduce((sum, item) => sum + item.total_score, 0) / scores.length).toFixed(2)
          : 0}
      </div>
      <table className="session-table">
        <thead>
          <tr>
            <th className="text-center capitalize" colSpan={4}>
              Association
            </th>
            {categories.map((category, index) => (
              <th
                key={`cat-${category.t}`}
                className="text-center capitalize"
                colSpan={category.p.length}
              >
                {category.t}
              </th>
            ))}
          </tr>
          <tr>
            <th className="text-center capitalize">Session</th>
            <th className="text-center capitalize">Student</th>
            <th className="text-center capitalize">Coach</th>
            <th className="text-center capitalize">Total</th>
            {categories.map((category) =>
              category.p.map((point) => (
                <th
                  key={`cat-${category.t}-${point}-th`}
                  className="text-center capitalize"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={point}
                >
                  {point.replaceAll(' ', '\n')}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {scores.map((score) => {
            const isNewScore =
              lookupScore(score.qa_score, 'welcome', 'coach life story').reason ===
              'No score found';

            return (
              <tr key={`score-${score.session_uuid}`}>
                <td className="text-center">
                  <a
                    href={`/sessiondetails?id=${score.session_uuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600 cursor-pointer underline"
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Session Details"
                  >
                    Session
                  </a>
                </td>
                <td className="text-center">
                  <a
                    href={`/studentprofile?id=${score.student_uuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tooltip-id="my-tooltip"
                    className="text-blue-500 hover:text-blue-600 cursor-pointer underline capitalize"
                    data-tooltip-content="Student Profile"
                  >
                    {score.student_name}
                  </a>
                </td>
                <td className="text-center">
                  <a
                    href={`/teacherprofile?id=${score.teacher_uuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Coach Profile"
                    className="text-blue-500 hover:text-blue-600 cursor-pointer underline capitalize"
                  >
                    {score.teacher_name}
                  </a>
                </td>
                <td
                  className="center"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content="Total Score"
                >
                  <span className="text-center">
                    {isNewScore ? score.total_score : calculateSum(score)}
                  </span>
                </td>
                {categories.map((category) =>
                  category.p.map((point) => {
                    const cps = lookupScore(score.qa_score, category.t, point);
                    return (
                      <td
                        key={`cat-${category.t}-${point}`}
                        className="center"
                        data-tooltip-id="my-tooltip"
                        data-tooltip-content={`${category.t}:${point}->${cps.reason}`}
                      >
                        <span className="text-center">{cps.score}</span>
                      </td>
                    );
                  })
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default GptSessionQaScores;
